import axios, { AxiosInstance } from 'axios';
import I18n from 'i18n-js';
import Toast from '../../components/shared/Toast';
import ErrorMessage from "../../models/ErrorMessage";
import SessionStorageConst from '../consts/SessionStorageConst';

// axios timeout doesn't catch missing urls, e.g. those with no response,
// so we use the axios cancel token to ensure the timeout
const { CancelToken } = axios;
const timeout = 60 * 1000; // 60 seconds

let axiosInstance: AxiosInstance;

export function getAxiosInstance(): AxiosInstance {
    if (axiosInstance == null) {
        axiosInstance = getNewAxiosInstance();
    }
    // @ts-ignore
    axiosInstance.cancelToken = new CancelToken((cancel) => setTimeout(() => cancel('Service not available'), timeout));

    return axiosInstance;
}

function getNewAxiosInstance() {
    axiosInstance = axios.create({
        timeout,
    });
    return setupInterceptors();
}

function setupInterceptors() {
    axiosInstance.interceptors.request.use(
        async (config) => {
            if (!config || !config.headers)
                return;

            config.headers['Content-Type'] = 'application/json';
            config.headers['Language'] = sessionStorage.getItem(SessionStorageConst.Language) as string;

            return config;
        },
        (error) => {
            return Promise.reject(error);
        },
    );

    // TODO: move error handling
    axiosInstance.interceptors.response.use(
        (response) => response,
        async (error) => {
            try {
                const shouldSuppressErrorToast = error.config?.suppressErrorToast;

                if (error.response.data) {
                    if (error.response.status === 500) {
                        if (!shouldSuppressErrorToast) {
                            Toast.notifyError(I18n.t('error.genericError'));
                        }
                        return Promise.reject(I18n.t('error.genericError'));
                    }
                    if (error.response.status === 401) {
                        return Promise.reject(I18n.t('error.sessionExpired'));
                    }
                    if (error.response.status !== 400) {
                        if (!shouldSuppressErrorToast) {
                            Toast.notifyError(I18n.t('error.genericError'));
                        }
                        return Promise.reject(I18n.t('error.genericError'));
                    }

                    const errorData = error.response.data;

                    if (!shouldSuppressErrorToast) {
                        handleErrorMessages(errorData);
                    }

                    return Promise.reject(errorData);
                }

                return Promise.reject(error);
            } catch (e) {
                return Promise.reject(error);
            }
        },
    );

    // @ts-ignore
    axiosInstance.spread = axios.spread;

    return axiosInstance;
}

function handleErrorMessages(errorData: any) {
    if (Array.isArray(errorData)) {
        const errorMessages = errorData as ErrorMessage[];
        errorMessages.filter(x => x.code).forEach(errorMessage => {
            Toast.notifyError(errorMessage.message);
        });
    }
    else {
        const errorMessage = errorData as ErrorMessage;
        if (errorMessage.message) {
            Toast.notifyError(errorMessage.message);
        }
        else {
            Toast.notifyError(errorData as string);
        }
    }
}
